import { formatPhoneNumber } from '@/services/formatter';
import requestResponsesCounter, {
  type TRequestResponseStatusCounts
} from '@/services/helpers/requestResponsesCounter';
import { autoCredMapper } from '@/services/mappers/autoCredMapper';
import { type TRequestDetailed } from '@/types/request';

import {
  type TClient,
  type TClientApi,
  type TClientComment,
  type TClientCommentApi,
  type TClientDetailed,
  type TClientDetailedApi,
  type TClientRequest,
  type TClientRequestApi,
  type TClientRequestUser,
  type TClientRequestUserApi
} from '../types';

export const clientsMapperFromApi = (data: TClientApi | null): TClient => {
  if (!data)
    return {
      birthDate: '',
      clientIin: '',
      commentForUser: '',
      comments: [],
      createDate: '',
      firstName: '',
      genderCode: null,
      lastName: '',
      lastRequestCreationDate: '',
      middleName: '',
      phoneNumber: '',
      status: null,
      uuid: ''
    };
  return {
    birthDate: data.birth_date ?? '',
    clientIin: data.client_iin,
    commentForUser: data.comment_for_user ?? '',
    comments: (data.comments ?? []).map(clientCommentsMapperFromApi),
    createDate: data.create_date,
    firstName: data.first_name ?? '',
    genderCode: data.gender?.code ?? null,
    lastName: data.last_name ?? '',
    lastRequestCreationDate: data.last_request_creation_date ?? '',
    middleName: data.middle_name ?? '',
    phoneNumber: formatPhoneNumber(data.phone_number),
    status: data.status,
    uuid: data.uuid
  };
};

export const clientsDetailedMapperFromApi = (
  data: TClientDetailedApi
): TClientDetailed => {
  return {
    birthDate: data.birth_date ?? '',
    clientIin: data.client_iin,
    createDate: data.create_date,
    firstName: data.first_name ?? '',
    gender: data.gender,
    lastName: data.last_name ?? '',
    middleName: data.middle_name ?? '',
    phoneNumber: formatPhoneNumber(data.phone_number),
    requests: autoCredMapper({
      requests: data.Requests as unknown as TRequestDetailed[],
      isDetailed: true
    }),
    requestsNewTyped: data.Requests.map(clientRequestsMapperFromApi),
    uuid: data.uuid
  };
};

const clientCommentsMapperFromApi = (
  data: TClientCommentApi
): TClientComment => {
  return data.Requests_Comments?.[0]?.comment ?? '';
};

const clientRequestsMapperFromApi = (
  data: TClientRequestApi
): TClientRequest => {
  return {
    carYear: data.car_year,
    code: data.code,
    createDate: data.create_date,
    dealer: data.dealer,
    initFee: data.init_fee,
    loanPeriodCode: data.loan_period_code,
    model: data.model,
    responseStatuses: clientRequestToResponseStatusesMapper(data),
    status: data.status,
    user: clientRequestUserMapperFromApi(data.user),
    uuid: data.uuid,
    vehicleCost: data.vehicle_cost
  };
};

const clientRequestToResponseStatusesMapper = (
  data: TClientRequestApi
): TRequestResponseStatusCounts => {
  return requestResponsesCounter(
    data.Responses.map((response) => ({
      bankName: response.bank?.name ?? '',
      fundedDate: response.funded_date,
      status: response.status?.code ?? ''
    })),
    data.status?.code ?? ''
  );
};

const clientRequestUserMapperFromApi = (
  data: TClientRequestUserApi | null
): TClientRequestUser | null => {
  if (!data) return null;
  return {
    firstName: data.first_name,
    lastName: data.last_name,
    middleName: data.middle_name
  };
};
